import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../utils/helpers";

export function matchesTransport(run, query) {
  const needle = query.trim().toLocaleLowerCase();
  return !needle || [run.meta?.packageName, run.meta?.packageId, ...(run.meta?.artifacts || [])]
    .some(value => typeof value === "string" && value.toLocaleLowerCase().includes(needle));
}

export default function useCicdHistory(spaceId, enabled, liveRuns = [], eventRevision = 0) {
  const [search, setSearch] = useState({ spaceId, query: "" });
  const query = search.spaceId === spaceId ? search.query : "";
  const [page, setPage] = useState({ key: "", offset: 0 });
  const key = JSON.stringify([spaceId, query]);
  const offset = page.key === key ? page.offset : 0;
  const [history, setHistory] = useState(null);
  const [pending, setPending] = useState(false);
  const [revision, setRevision] = useState(0);
  const sequence = useRef(0);
  useEffect(() => {
    // A fresh Space visit starts with page one, including when returning to it.
    setSearch({ spaceId, query: "" });
    setPage({ key: "", offset: 0 });
    setHistory(null);
  }, [spaceId]);
  useEffect(() => {
    const seq = ++sequence.current;
    if (!enabled || !spaceId) {
      setHistory(null);
      setPending(false);
      // An offset is valid only while the preceding pages are still retained.
      setPage(previous => previous.offset ? { key: "", offset: 0 } : previous);
      return;
    }
    setPending(true);
    const timer = setTimeout(() => {
      api(`/api/cicd/pipeline/history?spaceId=${encodeURIComponent(spaceId)}&q=${encodeURIComponent(query)}&limit=20&offset=${offset}`)
        .then(result => {
          if (sequence.current !== seq) return;
          setHistory(previous => ({ ...result, key, error: null,
            runs: offset && previous?.key === key ? [...previous.runs, ...result.runs] : result.runs }));
        }).catch(error => {
          if (sequence.current !== seq) return;
          setHistory(previous => ({ ...(previous?.key === key ? previous : { runs: [] }), key, error: error.message }));
        }).finally(() => { if (sequence.current === seq) setPending(false); });
    }, query ? 250 : 0);
    return () => { clearTimeout(timer); sequence.current++; };
  }, [spaceId, enabled, query, offset, key, revision, eventRevision]);
  const current = history?.key === key ? history : null;
  const runs = useMemo(() => {
    const merged = new Map((current?.runs || []).map(r => [String(r.id), r]));
    for (const run of liveRuns.filter(r => matchesTransport(r, query))) {
      const previous = merged.get(String(run.id));
      merged.set(String(run.id), { ...previous, ...run, id: String(run.id), meta: run.meta || previous?.meta,
        // Live observations replace archive status markers as well as the status.
        historyOnly: false, timedOut: !!run.timedOut });
    }
    return [...merged.values()].sort((a, b) => (Date.parse(b.createdAt || b.meta?.at) || 0) - (Date.parse(a.createdAt || a.meta?.at) || 0));
  }, [current, liveRuns, query]);
  return { runs, query, setQuery: value => { setSearch({ spaceId, query: value.slice(0, 200) }); setPage({ key: "", offset: 0 }); },
    // Until all pages arrive, GitHub-only rows cannot be counted against the unseen archive.
    total: current && !current.error && !current.hasMore ? runs.length : undefined, hasMore: !!current?.hasMore,
    loading: pending, error: current?.error || null,
    refresh: () => { setPage({ key, offset: 0 }); setRevision(value => value + 1); },
    loadMore: () => { if (!pending && current?.hasMore) { setPage({ key, offset: (current.offset || 0) + 20 }); setRevision(value => value + 1); } },
  };
}
